import { get, head, defaultTo } from 'lodash';

// local dependencies
import { HttpService } from 'core/modules/network-service';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION } from 'constants/common';
import { REDUCER } from 'constants/entity-names';
import { getStore } from 'store';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc CallDispositionNotes defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { CallDispositionNotesService } from 'services/callDispositionNotes';
 *
 * // create service instance and send/cancel request
 * const callDispositionNotesService = new CallDispositionNotesService();
 * const result = await callDispositionNotesService.fetchData( '<user-id>' ); // fetch data
 * callDispositionNotesService.cancel(); // cancel an active request
 */
export class CallDispositionNotesService extends HttpService {

	// make request and fetch data
	async fetchData( { url = {}, data = {} } = {} ) {
		try {

			// get ongoing call info from the redux store
            const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
            const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );

            // extract data
            const { memberId, patientId } = data;

			const response = await this.post( {
				host: CONFIG.host,
				path: url,
				data: {
					context: {
                        memberId,
                        callId,
                        callType,
                        operation: CRUD_OPERATION.READ,
                        patientId
					},
					data: {},
				},
			} );

			return {
				status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
				data: {
					items: get( response, 'body.component.data.state.items', [] ),
					actions: get( response, 'body.component.structure.actions', [] ),
					fields: get( response, 'body.component.structure.fields', [] ),
				},
			};
		} catch ( errResponse ) {
			return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
		}
	}

	// add or update title/Description item
	async addUpdateItem( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );

        // extract `operation` and actual payload
        const { operation, id = null, title, description, memberId } = data;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: {
                        callId,
                        callType,
                        operation,
                        memberId
                    },
                    data: {
                        id,
                        title,
                        description,
                    },
                }
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    id: defaultTo( head( get( response, 'body.component.data.itemIds', [] ) ), id ),
                    title,
                    description,
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // delete a title/Description item
    async deleteItem( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );

        // extract `operation` and actual payload
        const { id, memberId } = data;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: {
                        callId,
                        callType,
                        operation: CRUD_OPERATION.DELETE,
                        memberId
                    },
                    data: {
                        id
                    }
                }
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: null,
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

	// format error response
	_errorFormatter( errResponse ) {
		return errResponse; // some logic
	}
}
